import {
    assignQtyKey, contactUsHeaderKey,
    continueKey,
    costPerUnitKey,
    descriptionKey, discountPercentageKey,
    endDateKey, exportCartKey,
    genericErrorTitleKey,
    hostIdKey,
    licenseTypeKey,
    mainMessages,
    myAssignedProductsKey,
    myCartKey,
    productNumberKey,
    productsHostKey,
    quantityConsumedKey,
    quantityConsumedSessionKey, saveCart,
    startDateKey,
    totalCostKey
} from "../../assets/text/MultilingualText";
import React, {createRef, useContext, useEffect, useRef, useState} from "react";
import UserContext from "../common/UserContext";
import {AssignedProductContext, GeneralContext, SelectedProductContext} from "./FSARequestLicenseContext";
import {useLocalization} from "@progress/kendo-react-intl";
import {Button} from "@progress/kendo-react-buttons";
import {getHostDetails} from "../common/AssignHost/GenericAssignModal/utilities";
import AssignProductsModal from "./AssignProducts/AssignProductsModal";
import {AssignProductsGrid} from "./AssignProducts/AssignProductsGrid";
import {PanelBar, PanelBarItem} from "@progress/kendo-react-layout";
import PanelBarHeader from "./AssignProducts/PanelBarHeader";
import {AssignHostGrid} from "./AssignProducts/AssignHostGrid";
import {Tooltip} from "@progress/kendo-react-tooltip";
import {fileExcelIcon} from "@progress/kendo-svg-icons";
import {ExcelExport, ExcelExportColumn} from "@progress/kendo-react-excel-export";
import moment from "moment/moment";
import Alert from "../common/Alert";
import {formatCurrency} from "../common/utilities";

export default function AssignProducts(props) {
    const {
        siteLanguageDefault,
        accessToken
    } = useContext(UserContext);

    const {
        productNumKey,
        licenseStartDateKey,
        licenseEndDateKey,
        setIsLoading,
        showGenericError,
        setShowGenericError,
        showSecondGenericError,
        setShowSecondGenericError,
        showQuantityConsumedError,
        saveAssignChanges,
    } = useContext(GeneralContext);

    const {
        selectedProducts,
        setSelectedProducts,
    } = useContext(SelectedProductContext);

    const {
        assignSelectID: selectID,
        assignedProducts,
        setAssignedProducts,
        setAssignedHeaderCheck: setSelectHeaderCheck,
        sortByDisableValue,
        sortByDisableValue2,
        setEnableSortByDisable,
        unsavedAssignedChanges,
        sortByDisableID,
        sortByDisableID2
    } = useContext(AssignedProductContext);

    const localization = useLocalization();
    const scrollRef = useRef(null);
    const [enableAssignProducts, setEnableAssignProducts] = useState(false);

    //enable assign products button
    useEffect(() => {
        const selected = selectedProducts.filter(product => product[selectID] && product.unassigned_quantity > 0);
        const isValidQty = selected.every(product => typeof product.assign_quantity == "number" && product.assign_quantity > 0);
        setEnableAssignProducts(selected.length && isValidQty);
    }, [selectedProducts]);

    // assign host modal
    const [hostDetails, setHostDetails] = useState({});
    const assign = () => {
        //save changes parallel to getting host type details
        if (unsavedAssignedChanges.size > 0) {
            saveAssignChanges(1)
        }
        const selected = selectedProducts.filter(product => product[selectID]);
        const hostIDType = selected[0]['license_type'].filter(lt => lt.selected)[0][sortByDisableID];
        let partials = selected[0]['license_type'].filter(lt => lt.selected)[0][sortByDisableID2];
        if (partials === 'P') {
            setHostDetails({
                hostid_type_popup: 'PARTIAL'
            })
        } else {
            getHostDetails(accessToken, hostIDType, setHostDetails, setIsLoading, setShowSecondGenericError)
        }
    }

    const alertRef = useRef()
    useEffect(() => {
        if (showSecondGenericError) {
            alertRef.current.focus();
        }
    }, [showSecondGenericError]);

    // kendo react excel export
    const [userExcelData, setUserExcelData] = useState([]);
    const [excelFileName] = useState("KSM_FSA_ASSIGNEDHOSTS_" + moment(new Date()).format('YYYYMMDD HHmmss'));

    const _exporter = createRef();
    const exportExcel = () => {
        if (_exporter.current) {
            _exporter.current.save();
        }
    };

    useEffect(() => {
        if (assignedProducts.length) {
            let updates = [];
            assignedProducts.forEach(host => {
                host.products.forEach(product => {
                    updates.push({
                        product_number: product[productNumKey],
                        description: product.description,
                        license_type: product.license_type_text,
                        assign_quantity: product.assign_quantity,
                        start_date: moment(product[licenseStartDateKey]).format('MM/DD/YYYY'),
                        end_date: moment(product[licenseEndDateKey]).format('MM/DD/YYYY'),
                        host_id: product.host_id,
                        discount_percentage: product.discount_percentage.toString() + '%',
                        cost_per_unit: formatCurrency(product.price, product.currency_locale, product.currency_code),
                        total_cost:  formatCurrency(product.total_cost, product.currency_locale, product.currency_code),
                    });
                })
            })
            setUserExcelData(updates);
        }
    }, [assignedProducts]);

    return (
        <>
            <div className={'k-h5'}>
                {localization.toLanguageString(productsHostKey, mainMessages[siteLanguageDefault][productsHostKey])}
            </div>

            {assignedProducts.length > 0 && (
                <>
                    <div className={"k-h5"}>
                        {localization.toLanguageString(myAssignedProductsKey, mainMessages[siteLanguageDefault][myAssignedProductsKey])}
                    </div>

                    <PanelBar
                        expandMode={'single'}
                        className={'ksm-panelbar-default'}
                    >
                        {assignedProducts.map((host) => {
                            return (
                                <PanelBarItem
                                    title={
                                        <PanelBarHeader
                                            title={host.panel_bar_title}
                                            transactionID={host.transaction_id}
                                            setShowGenericError={setShowGenericError}
                                        />
                                    }
                                    key={host.transaction_id}
                                >
                                    <AssignHostGrid
                                        currentHost={host}
                                        setShowGenericError={setShowGenericError}
                                    />
                                </PanelBarItem>
                            )
                        })}
                    </PanelBar>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'end',
                        gap: '0.5rem'
                    }}>
                        <div style={{
                            display: 'flex',
                            gap: '0.5rem'
                        }}>
                            <Button
                                themeColor={"primary"}
                                size={"large"}
                                fillMode={"outline"}
                                rounded={"small"}
                                disabled={unsavedAssignedChanges.size === 0}
                                onClick={() => {
                                    saveAssignChanges(1);
                                }}
                            >
                                {localization.toLanguageString(saveCart, mainMessages[siteLanguageDefault][saveCart])}
                            </Button>
                            <Button
                                themeColor={"primary"}
                                size={"large"}
                                fillMode={"solid"}
                                onClick={() => {
                                    saveAssignChanges(2);
                                }}
                            >
                                {localization.toLanguageString(continueKey, mainMessages[siteLanguageDefault][continueKey])}
                            </Button>
                        </div>
                        <div>
                            <Tooltip
                                anchorElement="target"
                                showCallout={true}
                                parentTitle={true}
                                openDelay={0}
                                position="top"
                            >
                                <Button
                                    title={localization.toLanguageString(exportCartKey, mainMessages[siteLanguageDefault][exportCartKey])}
                                    themeColor={"tertiary"}
                                    fillMode={"solid"}
                                    size={"large"}
                                    onClick={exportExcel}
                                    svgIcon={fileExcelIcon}
                                />
                            </Tooltip>
                        </div>
                    </div>

                    <ExcelExport
                        data={userExcelData}
                        fileName={excelFileName}
                        ref={_exporter}
                    >
                        <ExcelExportColumn
                            field="product_number"
                            title={localization.toLanguageString(productNumberKey, mainMessages[siteLanguageDefault][productNumberKey])}
                        />
                        <ExcelExportColumn
                            field="description"
                            title={localization.toLanguageString(descriptionKey, mainMessages[siteLanguageDefault][descriptionKey])}
                        />
                        <ExcelExportColumn
                            field="assign_quantity"
                            title={localization.toLanguageString(assignQtyKey, mainMessages[siteLanguageDefault][assignQtyKey])}
                        />
                        <ExcelExportColumn
                            field="license_type"
                            title={localization.toLanguageString(licenseTypeKey, mainMessages[siteLanguageDefault][licenseTypeKey])}
                        />
                        <ExcelExportColumn
                            field="start_date"
                            title={localization.toLanguageString(startDateKey, mainMessages[siteLanguageDefault][startDateKey])}
                        />
                        <ExcelExportColumn
                            field="end_date"
                            title={localization.toLanguageString(endDateKey, mainMessages[siteLanguageDefault][endDateKey])}
                        />
                        <ExcelExportColumn
                            field="host_id"
                            title={localization.toLanguageString(hostIdKey, mainMessages[siteLanguageDefault][hostIdKey])}
                        />
                        <ExcelExportColumn
                            field="discount_percentage"
                            title={localization.toLanguageString(discountPercentageKey, mainMessages[siteLanguageDefault][discountPercentageKey])}
                        />
                        <ExcelExportColumn
                            field="cost_per_unit"
                            title={localization.toLanguageString(costPerUnitKey, mainMessages[siteLanguageDefault][costPerUnitKey])}
                        />
                        <ExcelExportColumn
                            field="total_cost"
                            title={localization.toLanguageString(totalCostKey, mainMessages[siteLanguageDefault][totalCostKey])}
                        />
                    </ExcelExport>

                    {showQuantityConsumedError &&
                        <Alert
                            type={'error'}
                            title={localization.toLanguageString(quantityConsumedKey, mainMessages[siteLanguageDefault][quantityConsumedKey])}
                            message={localization.toLanguageString(quantityConsumedSessionKey, mainMessages[siteLanguageDefault][quantityConsumedSessionKey])}
                        />
                    }

                    {showGenericError &&
                        <Alert
                            type={'error'}
                            title={localization.toLanguageString(genericErrorTitleKey, mainMessages[siteLanguageDefault][genericErrorTitleKey])}
                            message={localization.toLanguageString(contactUsHeaderKey, mainMessages[siteLanguageDefault][contactUsHeaderKey])}
                        />
                    }

                    <div className={'k-hr'} style={{margin: '1rem 0 1rem 0'}}/>

                </>
            )}

            {selectedProducts
                .filter((product) => product.unassigned_quantity > 0)
                .length > 0 && (
                <>
                    <div className={'k-h5'}>
                        {localization.toLanguageString(myCartKey, mainMessages[siteLanguageDefault][myCartKey])}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'start',
                        }}
                    >
                        <Button
                            themeColor={"primary"}
                            size={"large"}
                            fillMode={"solid"}
                            type={"button"}
                            disabled={!enableAssignProducts}
                            onClick={assign}
                        >
                            {localization.toLanguageString(productsHostKey, mainMessages[siteLanguageDefault][productsHostKey])}
                        </Button>
                    </div>

                    <AssignProductsGrid/>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: '0.5rem'
                        }}
                        tabIndex={0}
                        ref={alertRef}
                    >
                        {showSecondGenericError && (
                            <Alert
                                type={'error'}
                                showHandler={() => {
                                    setShowSecondGenericError(false)
                                }}
                                title={localization.toLanguageString(genericErrorTitleKey, mainMessages[siteLanguageDefault][genericErrorTitleKey])}
                                message={localization.toLanguageString(contactUsHeaderKey, mainMessages[siteLanguageDefault][contactUsHeaderKey])}
                            />
                        )}
                    </div>
                </>
            )}

            {Object.keys(hostDetails).length !== 0 &&
                <AssignProductsModal
                    selectID={selectID}
                    setIsLoading={setIsLoading}
                    scrollRef={scrollRef}
                    hostDetails={hostDetails}
                    setHostDetails={setHostDetails}
                    assignedProducts={assignedProducts}
                    setAssignedProducts={setAssignedProducts}
                    selectedProducts={selectedProducts}
                    setSelectedProducts={setSelectedProducts}
                    sortByDisableValue={sortByDisableValue}
                    sortByDisableValue2={sortByDisableValue2}
                    setSelectHeaderCheck={setSelectHeaderCheck}
                    setEnableSortByDisable={setEnableSortByDisable}
                />
            }
        </>
    )
}
